import React, { useEffect, useRef, useState } from 'react';
import { motion as Motion, AnimatePresence } from 'framer-motion';
import { Play, Volume2 } from 'lucide-react';

const FADE = { duration: 0.35, ease: [0.16, 1, 0.3, 1] };

/* Campaign teaser reel. Loops muted in place; the play mark restarts the reel
   from the top with sound (no modal), a second click mutes again. Only one
   video on the page is ever audible: turning sound on here mutes every other
   video, and any other video starting with sound mutes this one. Sound is also
   dropped when the reel scrolls out of view.

   `src` is a URL or a list of {src, type}; with a list the browser plays the
   first type it supports (HEVC where available, H.264 otherwise). */
const TeaserVideo = ({ src, poster }) => {
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;

    const mute = () => {
      if (video.muted) return;
      video.muted = true;
      setMuted(true);
    };

    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) mute();
    }, { threshold: 0.25 });
    observer.observe(video);

    // Media events don't bubble, so listen in the capture phase.
    const onOtherAudible = (e) => {
      if (e.target !== video && e.target instanceof HTMLMediaElement && !e.target.muted && !e.target.paused) mute();
    };
    document.addEventListener('play', onOtherAudible, true);
    document.addEventListener('volumechange', onOtherAudible, true);

    return () => {
      observer.disconnect();
      document.removeEventListener('play', onOtherAudible, true);
      document.removeEventListener('volumechange', onOtherAudible, true);
    };
  }, []);

  const toggleSound = () => {
    const video = videoRef.current;
    if (!video) return;
    if (!video.muted) {
      video.muted = true;
      setMuted(true);
      return;
    }
    document.querySelectorAll('video').forEach((other) => {
      if (other !== video) other.muted = true;
    });
    video.currentTime = 0;
    video.muted = false;
    video.play().catch(() => {});
    setMuted(false);
  };

  // `loop` alone can stall on long reels served with range requests; restart
  // explicitly as a fallback.
  const handleEnded = () => {
    const video = videoRef.current;
    if (!video) return;
    video.currentTime = 0;
    video.play().catch(() => {});
  };

  return (
    <Motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      viewport={{ once: true }}
      className="relative w-full rounded-lg md:rounded-2xl overflow-hidden cursor-pointer group border border-border-color"
      onClick={toggleSound}
      role="button"
      aria-label={muted ? 'Sound on' : 'Sound off'}
    >
      <video
        ref={videoRef}
        src={Array.isArray(src) ? undefined : src}
        poster={poster}
        autoPlay
        loop
        muted
        playsInline
        preload="auto"
        onEnded={handleEnded}
        className="w-full object-cover"
      >
        {Array.isArray(src) && src.map((s) => <source key={s.src} src={s.src} type={s.type} />)}
      </video>
      <AnimatePresence initial={false}>
        {muted ? (
          <Motion.div
            key="play"
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 1.15 }}
            transition={FADE}
            className="absolute inset-0 flex items-center justify-center pointer-events-none"
          >
            <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-black/50 backdrop-blur-md border border-white/20 flex items-center justify-center text-white group-hover:scale-110 transition-transform duration-300">
              <Play size={28} fill="white" />
            </div>
          </Motion.div>
        ) : (
          <Motion.div
            key="sound"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={FADE}
            className="absolute bottom-4 right-4 md:bottom-6 md:right-6 pointer-events-none"
          >
            <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-black/50 backdrop-blur-md border border-white/20 flex items-center justify-center text-white opacity-40 group-hover:opacity-100 transition-opacity duration-300">
              <Volume2 size={20} />
            </div>
          </Motion.div>
        )}
      </AnimatePresence>
    </Motion.div>
  );
};

export default TeaserVideo;
